import { Dispatch, FC, SyntheticEvent, forwardRef, useCallback, useState } from 'react';
import {
    Accordion, AccordionDetails, AccordionSummary, Box, Button, Card,
    CardActions, CardContent, Dialog, DialogActions, DialogContent,
    DialogContentText, DialogTitle, Divider, FormControlLabel, List, ListItem, ListItemText,
    Slide, Stack, Switch, TableCell, TableRow, Typography, styled, tableCellClasses
} from '@mui/material';
import PersonRemoveIcon from '@mui/icons-material/PersonRemove';
import BorderColorIcon from '@mui/icons-material/BorderColor';
import RestoreIcon from '@mui/icons-material/Restore';
import DeleteIcon from "@mui/icons-material/Delete";
import ExitToAppIcon from '@mui/icons-material/ExitToApp';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import UserForm from '../../../../../forms/UserForm';
import { AlertType } from '../../../../../../types/AlertType';
import { DataRef } from '../../../../../../types/DataRef';
import { TransitionProps } from '@mui/material/transitions';
import { LoadingButton } from '@mui/lab';
import { DataProfil } from '../../../../../../types/DataTypes';
import Cookies from 'universal-cookie';
import axios, { AxiosInstance } from 'axios';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { loggoutUser } from '../../../../../../redux/actions/logginUser';
import OptionalWidthModal from '../../../../../../commons/optional-width-modal/OptionalWidthModal';
import { DataUtilisateur } from './Utilisateur';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { profilToAccessCommissionCommercialGRC } from '../../../../../../constants/Constants';
import CommissionForm from '../../../../../forms/commission/CommissionForm';

interface CustomeBtnParam {
    bgColor: string;
    hoverColor?: string;
    color?: string;
}

interface UtilisateurRowProps {
    isDesktopOrLaptop: boolean;
    utilisateur: DataUtilisateur;
    dataPays: DataRef[];
    dataProfils: DataProfil[];
    reloadList: () => void;
    expanded: number | false;
    setExpanded: Dispatch<React.SetStateAction<number | false>>;
    setOpenSnack: Dispatch<React.SetStateAction<boolean>>;
    setAlerteMessage: Dispatch<React.SetStateAction<string>>;
    setAlertType: Dispatch<React.SetStateAction<AlertType>>;
}


export const btnStyle = (colors: CustomeBtnParam) => {
    return {
        backgroundColor: colors?.bgColor,
        color: colors?.color ? colors?.color : "#fff",
        fontSize: 13,
        textTransform: 'none',
        '&:hover': {
            backgroundColor: colors?.hoverColor
        },
    }
}

const StyledTableCell = styled(TableCell)(() => ({
    [`&.${tableCellClasses.body}`]: {
        fontSize: 12,
        textAlign: 'center',
        color: "#fff"
    },
}));

const Transition = forwardRef(function Transition(
    props: TransitionProps & {
        children: React.ReactElement<any, any>;
    },
    ref: React.Ref<unknown>,
) {
    return <Slide direction="up" ref={ref} {...props} />;
});

const urlBaseApi = process.env.REACT_APP_BASE_API_URL as string;

const UtilisateurRow: FC<UtilisateurRowProps> = ({
    isDesktopOrLaptop,
    utilisateur,
    dataPays,
    dataProfils,
    reloadList,
    expanded,
    setExpanded,
    setOpenSnack,
    setAlertType,
    setAlerteMessage }) => {
    const [openModal, setOpenModal] = useState<boolean>(false);
    const [deleteLoading, setDeleteLoading] = useState<boolean>(false);
    const [refreshLoading, setRefreshLoading] = useState<boolean>(false);
    const [unlockLoading, setUnlockLoading] = useState<boolean>(false);
    const [openModalDelete, setOpenModalDelete] = useState<boolean>(false);
    const [openModalRefresh, setOpenModalRefresh] = useState<boolean>(false);
    const [openModalUnlock, setOpenModalUnlock] = useState<boolean>(false);
    const [openModalCommission, setOpenModalCommission] = useState<boolean>(false);
    const [isCommercialGrc, setIsCommercialGrc] = useState<boolean>(false);

    const [isLocked, setIsLocked] = useState<boolean>(utilisateur.actif === "Non" || utilisateur.failed_attempts >= 5);

    const cookies = new Cookies();
    const token: string = cookies.get("jwt");

    const http: AxiosInstance = axios.create({
        baseURL: urlBaseApi,
        headers: {
            Authorization: `Bearer ${token}`,
        },
    });
    const navigate = useNavigate();
    const dispatch = useDispatch();


    // Add global response interceptor
    http.interceptors.response.use(
        (response) => {
            // Modify response data here, if needed
            return response;
        },
        (error) => {
            console.log(error.message);
            if (error.message === "Request failed with status code 401") {
                // Remove the redux store
                dispatch(loggoutUser());

                // Remove token in navigator cookies
                cookies.remove('jwt', { path: '/' });

                navigate("/sign-in");
            }
            return Promise.reject(error);
        }
    );

    const handleClose = () => {
        setOpenModal(false);
    };

    const handleCloseDeleteModal = () => {
        setOpenModalDelete(false);
    };

    const handleCloseRefreshModal = () => {
        setOpenModalRefresh(false);
    };

    const handleCloseCommission = () => {
        setOpenModalCommission(false);
    }

    const handleCloseUnlockModal = () => {
        setOpenModalUnlock(false);
    };

    const handleChangeAccordionValue = (panel: number) => (event: SyntheticEvent, isExpanded: boolean) => {
        setExpanded(isExpanded ? panel : false);
    };

    const handleDelete = useCallback(() => {
        setDeleteLoading(true);
        http.delete(`user/delete/${utilisateur?.id}`).then(res => {
            setOpenModalDelete(false);
            setDeleteLoading(false);
            setAlerteMessage("Suppresion efféctuée !");
            setAlertType("success");
            setOpenSnack(true);
            reloadList();
        }).catch(error => {
            console.log(error);
            setAlertType("error");
            setOpenSnack(true);
        });
    }, []);

    const handleRefresh = useCallback(() => {
        setRefreshLoading(true);
        http.get(`user/password/refresh/${utilisateur?.id}`).then(() => {
            setOpenModalRefresh(false);
            setRefreshLoading(false);
            setAlerteMessage("Réinitialisation du mot de passe réussi !");
            setAlertType("success");
            setOpenSnack(true);
            reloadList();
        }).catch(error => {
            console.log(error);
            setAlertType("error");
            setOpenSnack(true);
        });
    }, []);

    const handleToggleLock = () => {
        setUnlockLoading(true);
        http.get(`user/unlock/${utilisateur?.id}`).then(res => {
            setIsLocked(!isLocked);
            setOpenModalUnlock(false);
            setAlerteMessage(res?.data?.message);
            setAlertType("success");
            setOpenSnack(true);
            reloadList();
        }).catch(error => {
            console.log(error);
            setAlertType("error");
            setOpenSnack(true);
        }).finally(() => {
            setUnlockLoading(false);
        });
    };

    const usersPays = () => {
        const tabPays: string[] = [];
        for (let pays of utilisateur?.pays) {
            tabPays.push(pays?.libelle);
        }

        return tabPays.join(' / ');
    }

    const userProfil = () => {
        const tabProfiles: string[] = [];

        for (let profile of utilisateur?.profils) {
            tabProfiles.push(profile?.libelle);
            if (profilToAccessCommissionCommercialGRC?.includes(profile?.libelle as string) && !isCommercialGrc) {
                setIsCommercialGrc(true)
            };
        }

        return tabProfiles.join(' / ');
    }

    return (
        <>
            {isDesktopOrLaptop ? (
                <TableRow>
                    <StyledTableCell>{utilisateur?.name}</StyledTableCell>
                    <StyledTableCell>{utilisateur?.prenom}</StyledTableCell>
                    <StyledTableCell>{utilisateur?.email}</StyledTableCell>
                    <StyledTableCell>{usersPays()}</StyledTableCell>
                    <StyledTableCell>{utilisateur?.Departement}</StyledTableCell>
                    <StyledTableCell>{userProfil()}</StyledTableCell>
                    <StyledTableCell>
                        <FormControlLabel
                            control={
                                <Switch
                                    size="small"
                                    checked={isLocked}
                                    onChange={() => setOpenModalUnlock(true)}
                                    sx={{
                                        '& .MuiSwitch-switchBase.Mui-checked': {
                                            color: '#fff',
                                        },
                                        '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                                            backgroundColor: '#d32f2f',
                                            opacity: 1,
                                        },
                                        '& .MuiSwitch-switchBase + .MuiSwitch-track': {
                                            backgroundColor: '#ed6c02',
                                            opacity: 1,
                                        },
                                    }}
                                />
                            }
                            label={
                                <Typography
                                    sx={{
                                        fontSize: 12,
                                        // color: isLocked ? "error.main" : "warning.main",
                                        transition: "color 0.2s ease"
                                    }}
                                >
                                    {isLocked
                                        ? "Verrouillé"
                                        : "Actif"}
                                </Typography>
                            }
                        />
                    </StyledTableCell>
                    <StyledTableCell>
                        <Stack direction="row" spacing={1} display="flex" justifyContent="center">
                            <Button
                                size="small"
                                variant="outlined"
                                sx={btnStyle({
                                    bgColor: "#070d23",
                                    hoverColor: "Gray",
                                    color: "#fff"
                                })}
                                startIcon={<BorderColorIcon />}
                                onClick={() => setOpenModal(true)}
                            >
                                Modifier
                            </Button>
                            <Button
                                size="small"
                                variant="contained"
                                sx={btnStyle({
                                    bgColor: "#FF0000",
                                    hoverColor: "#FE3737"
                                })}
                                startIcon={<PersonRemoveIcon />}
                                onClick={() => setOpenModalDelete(true)}
                            >
                                Supprimer
                            </Button>
                            <Button
                                size="small"
                                variant="contained"
                                sx={btnStyle({
                                    bgColor: "#fff",
                                    hoverColor: "#F0F0F0",
                                    color: "#070d23"
                                })}
                                startIcon={<RestoreIcon />}
                                onClick={() => setOpenModalRefresh(true)}
                            >
                                Reinitialiser
                            </Button>
                            {
                                isCommercialGrc &&
                                <Button
                                    size="small"
                                    variant="contained"
                                    sx={btnStyle({
                                        bgColor: "#fff",
                                        hoverColor: "#F0F0F0",
                                        color: "#070d23"
                                    })}
                                    startIcon={<VisibilityIcon />}
                                    onClick={() => setOpenModalCommission(true)}
                                >
                                    Voir Commission
                                </Button>
                            }
                        </Stack>
                    </StyledTableCell>
                </TableRow >
            ) : (
                <Accordion
                    expanded={expanded === utilisateur?.id}
                    onChange={handleChangeAccordionValue(utilisateur?.id as number)}
                    sx={{
                        borderRadius: 1,
                        bgcolor: '#fff',
                        mb: 1
                    }}>
                    <AccordionSummary
                        expandIcon={<ExpandMoreIcon sx={{ color: '#080d24' }} />}
                        aria-controls="panel1-content"
                        id="panel1-header"
                    >
                        <Stack direction="column">
                            <List sx={{ py: 0 }} dense>
                                <ListItem sx={{ my: 0, py: 0 }} dense>
                                    <ListItemText
                                        primary="Nom :"
                                        secondary={
                                            <Typography sx={{ fontSize: 12, color: '#FC8404' }}>
                                                {utilisateur?.name}
                                            </Typography>
                                        }
                                    />
                                </ListItem>
                                <ListItem sx={{ my: 0, py: 0 }} dense>
                                    <ListItemText
                                        primary="Prénom :"
                                        secondary={
                                            <Typography sx={{ fontSize: 12, color: '#FC8404' }}>
                                                {utilisateur?.prenom}
                                            </Typography>
                                        }
                                    />
                                </ListItem>
                                <ListItem sx={{ my: 0, py: 0 }} dense>
                                    <ListItemText
                                        primary="Email :"
                                        secondary={
                                            <Typography sx={{ fontSize: 12, color: '#FC8404' }}>
                                                {utilisateur?.email}
                                            </Typography>
                                        }
                                    />
                                </ListItem>
                            </List>
                        </Stack>
                    </AccordionSummary>
                    <AccordionDetails>
                        <Stack direction='column' spacing={1}>
                            <Button
                                variant="contained"
                                sx={btnStyle({
                                    bgColor: "Gray",
                                    hoverColor: "Gray",
                                    color: "#fff",
                                })}
                                size='small'
                                fullWidth
                                startIcon={<BorderColorIcon />}
                                onClick={() => setOpenModal(true)}
                            >
                                Modifier
                            </Button >
                            <Button
                                size="small"
                                variant="contained"
                                sx={btnStyle({
                                    bgColor: "#FF0000",
                                    hoverColor: "#FE3737",
                                })}
                                fullWidth
                                startIcon={<DeleteIcon />}
                                onClick={() => setOpenModalDelete(true)}
                            >
                                Supprimer
                            </Button>

                            <Box mt={5}>
                                <Typography
                                    gutterBottom
                                    variant="h6"
                                    component="div"
                                    sx={{ mb: 0 }}
                                >
                                    Détails
                                </Typography>
                                <Divider
                                    orientation="horizontal"
                                    sx={{ bgcolor: "#fff", mb: 1 }}
                                />
                            </Box>

                            <Card>
                                <CardContent>
                                    <List dense>
                                        <ListItem dense>
                                            <ListItemText
                                                primary="Pays :"
                                                secondary={usersPays()}
                                            />
                                        </ListItem>
                                        <Divider
                                            orientation="horizontal"
                                            sx={{ bgcolor: " #dfdfdf" }}
                                        />
                                        <ListItem dense>
                                            <ListItemText
                                                primary="Département :"
                                                secondary={utilisateur?.Departement}
                                            />
                                        </ListItem>
                                        <Divider
                                            orientation="horizontal"
                                            sx={{ bgcolor: " #dfdfdf" }}
                                        />
                                        <ListItem dense>
                                            <ListItemText
                                                primary="Profil"
                                                secondary={userProfil()}
                                            />
                                        </ListItem>
                                    </List>
                                </CardContent>
                                <CardActions>
                                    <Button
                                        size="small"
                                        startIcon={<RestoreIcon />}
                                        color="warning"
                                        sx={{ textTransform: 'none' }}
                                        onClick={() => setOpenModalRefresh(true)}
                                    >
                                        Reinitialiser le mot passe
                                    </Button>
                                </CardActions>
                            </Card>
                        </Stack>
                    </AccordionDetails>
                </Accordion>
            )}

            <OptionalWidthModal
                title="Modifier un utilisateur"
                breackPoint="md"
                onClose={handleClose}
                open={openModal}
            >
                <UserForm
                    dataPays={dataPays}
                    dataProfils={dataProfils}
                    utilisateur={utilisateur}
                    onClose={handleClose}
                    setOpenSnack={setOpenSnack}
                    setAlerteMessage={setAlerteMessage}
                    setAlertType={setAlertType}
                    reloadList={reloadList}
                />
            </OptionalWidthModal>

            <OptionalWidthModal
                title="Voir Commission"
                breackPoint="xl"
                onClose={handleCloseCommission}
                open={openModalCommission}
            >
                <CommissionForm utilisateur={utilisateur} />
            </OptionalWidthModal>

            <Dialog
                open={openModalDelete}
                TransitionComponent={Transition}
                keepMounted
                onClose={handleCloseDeleteModal}
                aria-describedby="alert-dialog-slide-description"
            >
                <DialogTitle>{"Confirmation"}</DialogTitle>
                <DialogContent>
                    <DialogContentText id="alert-dialog-slide-description">
                        Vous êtes sur le point de supprimer cet utilisateur,
                        êtes-vous sûr de vouloir poursuivre cette action ?
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <LoadingButton
                        onClick={() => handleDelete()}
                        endIcon={<PersonRemoveIcon />}
                        loading={deleteLoading}
                        loadingPosition="end"
                        sx={{ textTransform: "none" }}
                        variant="contained"
                        color="warning"
                    >
                        Oui
                    </LoadingButton>
                    <Button
                        onClick={handleCloseDeleteModal}
                        variant="contained"
                        color="inherit"
                        sx={{ textTransform: "none" }}
                        startIcon={<ExitToAppIcon />}
                    >
                        Non
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog
                open={openModalRefresh}
                TransitionComponent={Transition}
                keepMounted
                onClose={handleCloseRefreshModal}
                aria-describedby="alert-dialog-slide-description"
            >
                <DialogTitle>{"Confirmation"}</DialogTitle>
                <DialogContent>
                    <DialogContentText id="alert-dialog-slide-description">
                        <p>Vous êtes sur le point de reinitialiser le mot de passe de ce client,</p>
                        <p>êtes-vous sûr de vouloir poursuivre cetteaction ?</p>
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <LoadingButton
                        onClick={() => handleRefresh()}
                        endIcon={<RestoreIcon />}
                        loading={refreshLoading}
                        loadingPosition="end"
                        sx={{ textTransform: "none" }}
                        variant="contained"
                        color="warning"
                    >
                        Oui
                    </LoadingButton>
                    <Button
                        onClick={handleCloseRefreshModal}
                        variant="contained"
                        color="inherit"
                        sx={{ textTransform: "none" }}
                        startIcon={<ExitToAppIcon />}
                    >
                        Non
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog
                open={openModalUnlock}
                TransitionComponent={Transition}
                keepMounted
                onClose={handleCloseUnlockModal}
                aria-describedby="alert-dialog-slide-description"
            >
                <DialogTitle>{"Confirmation"}</DialogTitle>
                <DialogContent>
                    <DialogContentText id="alert-dialog-slide-description">
                        Vous êtes sur le point de {isLocked ? "déverrouiller" : "verrouiller"} cet utilisateur,
                        êtes-vous sûr de vouloir poursuivre cette action ?
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <LoadingButton
                        onClick={() => handleToggleLock()}
                        endIcon={<PersonRemoveIcon />}
                        loading={unlockLoading}
                        loadingPosition="end"
                        sx={{ textTransform: "none" }}
                        variant="contained"
                        color="warning"
                    >
                        Oui
                    </LoadingButton>
                    <Button
                        onClick={handleCloseUnlockModal}
                        variant="contained"
                        color="inherit"
                        sx={{ textTransform: "none" }}
                        startIcon={<ExitToAppIcon />}
                    >
                        Non
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

export default UtilisateurRow;